import { useState, useEffect } from 'react';
import type { IAsset } from '../providers';
import { ZprMultiSelect } from '@zpr-stencil';
import { useAssetActions } from '../context/AssetActionsContext';
import { ZprAccordion, ZprAccordionPanel, ZprLabel } from '@zpr-stencil';
import { useLabels } from '../hooks';
import { useTheme } from '../context/ThemeContext';

export interface LabelsAccordionProps {
  asset: IAsset;
  isEditableAsset?: boolean;
  canEditLabels?: boolean;
  canAssignLabels?: boolean;
  updateAssetInList?: (asset: IAsset) => void;
}

export const LabelsAccordion = ({ asset, isEditableAsset = true, canEditLabels = true, canAssignLabels = true, updateAssetInList }: LabelsAccordionProps) => {
  const { zapworksProvider, workspaceId, onUpdateLabels, onCreateLabel, onEditLabel, onDeleteLabel } = useAssetActions();
  const { labels: availableLabels, getLabelNameByUid, getLabelObjectByUid, createLabel, updateLabel, deleteLabel } = useLabels(zapworksProvider, workspaceId);
  const theme = useTheme();
  const metadata = asset.getMetadata();
  const initialUids = Array.isArray(metadata.labels) ? metadata.labels.map((l: any) => l.uid as string) : [];

  const [selectedUids, setSelectedUids] = useState<string[]>(initialUids);

  useEffect(() => {
    const meta = asset.getMetadata();
    const uids = Array.isArray(meta.labels) ? meta.labels.map((l: any) => l.uid as string) : [];
    setSelectedUids(uids);
  }, [asset]);

  const handleLabelsChange = async (uids: string[]) => {
    setSelectedUids(uids);
    const updatedAsset = await onUpdateLabels?.(asset, availableLabels.filter(l => uids.includes(l.uid)));
    if (updatedAsset) {
      updateAssetInList?.(updatedAsset);
    }
  };

  const handleRemoveLabel = async (uid: string) => {
    const newUids = selectedUids.filter(id => id !== uid);
    setSelectedUids(newUids);
    const updatedAsset = await onUpdateLabels?.(asset, availableLabels.filter(l => newUids.includes(l.uid)));
    if (updatedAsset) {
      updateAssetInList?.(updatedAsset);
    }
  };

  return (
    <ZprAccordion className="no-background">
      <ZprAccordionPanel isOpen={true}>
        <span slot="accordion-header">Labels</span>
        <div slot="accordion-body">
          {selectedUids.length > 0 &&
            <div className="asset-detail-panel__section">
              <div className="labels-container">
                  {selectedUids.map((uid) => (
                    <ZprLabel
                      key={uid}
                      text={getLabelNameByUid(uid)}
                      color={getLabelObjectByUid(uid)?.color}
                      size='small'
                      canRemove={canAssignLabels}
                      onLabelRemove={() => handleRemoveLabel(uid)}
                    />
                  ))}
              </div>
            </div>
          }
          {isEditableAsset && (
            <div className="asset-detail-panel__section">
              <ZprMultiSelect
                placeholder="Manage labels"
                heading="Labels"
                options={availableLabels.map(label => ({
                  value: label.uid,
                  label: label.name,
                  selected: selectedUids.includes(label.uid),
                  color: label.color,
                }))}
                canToggleOption={canAssignLabels}
                onSelectionchanged={(e) => handleLabelsChange(e.detail)}
                enableSearch
                showLabelOnly={true}
                isLabelOptions
                direction='left'
                creatable={canEditLabels}
                onCreateOption={canEditLabels ? (e) => createLabel({ name: e.detail, color: '#EAEAEA' }).then(() => onCreateLabel?.(e.detail)) : undefined}
                editable={canEditLabels}
                deletable={canEditLabels}
                onOptionEdit={async (e) => {
                  const { value, label: newLabel, color: newColor } = e.detail;
                  await updateLabel(value, { name: newLabel ?? '', color: newColor ?? '' });
                  onEditLabel?.(value, newLabel ?? '');
                }}
                onOptionDelete={async (e) => {
                  const { value } = e.detail;
                  await deleteLabel(value);
                  onDeleteLabel?.(value);
                }}
                theme={theme}
                dropdownZIndex="1001"
              />
            </div>
          )}
        </div>
      </ZprAccordionPanel>
    </ZprAccordion>
  );
};
